import { InfoCircleOutlined } from '@ant-design/icons';
import { InventoryPurchaseConfig, InventoryUOMMapping, PurchaseBundleType } from '@gooduncles/gu-app-schema';
import { Button, Input, InputNumber, Select, Tooltip, message } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { purchaseBundleToKr } from 'src/constants';
import { formatNumber } from 'src/utils/common';
import { updateProductInventory } from 'src/utils/firebase/firebase-short-cut';
import { getPurchasePreview, inventoryPurchaseConfigTooltipMap } from 'src/utils/product-inventory-util';

import CardItem from 'src/atomic-components/atoms/CardItem/CardItem';
import CardTitle from 'src/atomic-components/atoms/CardItem/CardTitle';
import Label from 'src/atomic-components/atoms/TextItem/Label';
import Value from 'src/atomic-components/atoms/TextItem/Value';

const getPurchasePreviewForUnit = (config: InventoryPurchaseConfig, uom: InventoryUOMMapping) => {
  return Object.entries(uom).reduce((acc, [unit, { ratio: uomRatio }]) => {
    const previewMessage = getPurchasePreview(config, uomRatio * 10);
    return acc + `${unit}: ${previewMessage}\n`;
  }, '주문량 10개 기준\n');
};

interface PurchaseConfigInfoProps {
  inventoryId: string;
  uom: InventoryUOMMapping;
  purchaseConfig: InventoryPurchaseConfig;
  onRefresh: () => void;
}

const PurchaseConfigInfo: FC<PurchaseConfigInfoProps> = ({ inventoryId, uom, purchaseConfig, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editedConfig, setEditedConfig] = useState(purchaseConfig);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      await updateProductInventory(inventoryId, { purchaseConfig: editedConfig });
    } catch (error) {
      message.error('발주 설정 수정에 실패했습니다.');
    } finally {
      setLoading(false);
      setIsEditing(false);
      onRefresh();
    }
  }, [editedConfig, inventoryId, onRefresh]);

  const handleCancel = () => {
    setIsEditing(false);
    setEditedConfig(purchaseConfig);
  };

  useEffect(() => {
    setEditedConfig(purchaseConfig);
  }, [purchaseConfig]);

  return (
    <div>
      <CardItem>
        <CardTitle title='발주설정'>
          <Tooltip title={getPurchasePreviewForUnit(purchaseConfig, uom)} overlayStyle={{ whiteSpace: 'pre-line' }}>
            <InfoCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </CardTitle>
        {!isEditing ? (
          <Button size='small' onClick={handleEdit}>
            수정
          </Button>
        ) : (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button type='primary' size='small' onClick={handleSave} loading={loading}>
              저장
            </Button>
            <Button size='small' onClick={handleCancel} loading={loading}>
              취소
            </Button>
          </div>
        )}
      </CardItem>
      <CardItem>
        <Label>발주품명</Label>
        {isEditing ? (
          <Input
            className='flex1'
            value={editedConfig.purchaseProductName ?? ''}
            onChange={(e) => setEditedConfig({ ...editedConfig, purchaseProductName: e.target.value })}
          />
        ) : (
          <Value>{purchaseConfig.purchaseProductName}</Value>
        )}
      </CardItem>
      <CardItem>
        <Label>
          *묶음방식
          <Tooltip title={inventoryPurchaseConfigTooltipMap.purchaseBundle} overlayStyle={{ whiteSpace: 'pre-line' }}>
            <InfoCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </Label>
        {isEditing ? (
          <Select
            className='flex1'
            value={editedConfig.purchaseBundle}
            onChange={(value) => setEditedConfig({ ...editedConfig, purchaseBundle: value })}>
            {Object.values(PurchaseBundleType).map((type) => (
              <Select.Option key={type} value={type}>
                {purchaseBundleToKr[type as keyof typeof purchaseBundleToKr]}
              </Select.Option>
            ))}
          </Select>
        ) : (
          <Value>{purchaseBundleToKr[purchaseConfig.purchaseBundle]}</Value>
        )}
      </CardItem>
      <CardItem>
        <Label>단위 변환 값</Label>
        {isEditing ? (
          <InputNumber
            className='flex1'
            value={editedConfig.purchaseMultiplyUnit ?? 0}
            onChange={(value) => setEditedConfig({ ...editedConfig, purchaseMultiplyUnit: value ?? 0 })}
          />
        ) : (
          <Value>{formatNumber(purchaseConfig.purchaseMultiplyUnit)}</Value>
        )}
      </CardItem>
      <CardItem>
        <Label>최대 묶음</Label>
        {isEditing ? (
          <InputNumber
            className='flex1'
            value={editedConfig.purchaseMaxBundle ?? 0}
            onChange={(value) => setEditedConfig({ ...editedConfig, purchaseMaxBundle: value ?? 0 })}
          />
        ) : (
          <Value>{formatNumber(purchaseConfig.purchaseMaxBundle, 2, '-')}</Value>
        )}
      </CardItem>
      <CardItem>
        <Label>단위</Label>
        {isEditing ? (
          <Input
            className='flex1'
            value={editedConfig.purchaseUnit ?? ''}
            onChange={(e) => setEditedConfig({ ...editedConfig, purchaseUnit: e.target.value })}
          />
        ) : (
          <Value>{purchaseConfig.purchaseUnit ?? '개'}</Value>
        )}
      </CardItem>
    </div>
  );
};

export default PurchaseConfigInfo;
